import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { dbg, dbgErr } from '../lib/debug';
import DownloadButton from '../components/DownloadButton';
import { safeText } from '../lib/render';

const TABS = [
  { key: 'timeseries', label: 'Time Series', icon: 'fa-chart-line' },
  { key: 'dissertations', label: 'Dissertations', icon: 'fa-graduation-cap' },
  { key: 'assignments', label: 'Assignments', icon: 'fa-file-alt' },
];

export default function Samples() {
  const { user } = useAuth();
  const [active, setActive] = useState('timeseries');
  const [data, setData] = useState({ timeseries: [], dissertations: [], assignments: [] });
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dbg('page', 'Samples mounted — fetching /api/samples');
    (async () => {
      try {
        const res = await api.get('/api/samples');
        setData({
          timeseries: res.timeseries || [],
          dissertations: res.dissertations || res.samples || [],
          assignments: res.assignments || [],
        });
      } catch (e) {
        dbgErr('err', 'Samples load failed', e);
        setErr(e instanceof ApiError ? e.message : 'Failed to load samples');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const counts = {
    timeseries: data.timeseries.length,
    dissertations: data.dissertations.length,
    assignments: data.assignments.length,
  };
  const items = data[active] || [];

  return (
    <>
      <section className="hero-gradient py-5">
        <div className="container py-4" style={{ position: 'relative' }}>
          <div className="text-center text-white">
            <span className="badge bg-info text-dark px-3 py-2 mb-3" style={{ fontSize: '0.82rem' }}>
              <i className="fas fa-book-open me-1"></i>Real outputs
            </span>
            <h1 style={{ fontWeight: 800, fontSize: '2.8rem' }}>Samples</h1>
            <p className="lead" style={{ color: 'rgba(255,255,255,0.75)', maxWidth: 640, margin: '0 auto' }}>
              Real dissertations, assignments, and time-series analyses produced on
              Dissertation Island. Browse, open, and download to see what's possible.
            </p>
          </div>
        </div>
      </section>

      <div className="main-container">
        {!user && (
          <div
            className="mb-4 p-4"
            style={{
              background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
              color: '#fff',
              borderRadius: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '1rem',
            }}
          >
            <div>
              <h5 className="mb-1" style={{ fontWeight: 700 }}>
                Want outputs like these?
              </h5>
              <p className="mb-0" style={{ opacity: 0.9 }}>
                Sign up and generate your own — in minutes.
              </p>
            </div>
            <Link
              to="/register"
              className="btn"
              style={{ background: '#fff', color: 'var(--primary)', fontWeight: 700 }}
            >
              <i className="fas fa-user-plus me-1"></i>Sign up free
            </Link>
          </div>
        )}

        {/* Tabs */}
        <ul className="nav nav-pills mb-4 flex-wrap gap-2" role="tablist">
          {TABS.map((t) => (
            <li className="nav-item" key={t.key} role="presentation">
              <button
                type="button"
                className={`nav-link ${active === t.key ? 'active' : ''}`}
                onClick={() => setActive(t.key)}
              >
                <i className={`fas ${t.icon} me-1`}></i>
                {t.label}
                <span
                  className="ms-2 badge"
                  style={{
                    background: active === t.key ? 'rgba(255,255,255,0.25)' : 'var(--surface-tint, #eef)',
                    color: active === t.key ? '#fff' : 'var(--muted, #555)',
                    fontWeight: 600,
                  }}
                >
                  {counts[t.key]}
                </span>
              </button>
            </li>
          ))}
        </ul>

        {err && <div className="alert alert-danger">{err}</div>}
        {loading && <div className="text-muted">Loading samples…</div>}
        {!loading && !err && items.length === 0 && (
          <div className="text-center py-5">
            <i className={`fas ${TABS.find((t) => t.key === active)?.icon || 'fa-book-open'} fa-3x text-muted mb-3`}></i>
            <p className="text-muted">No {TABS.find((t) => t.key === active)?.label.toLowerCase()} samples yet.</p>
          </div>
        )}

        <div className="row g-4">
          {items.map((s) => (
            <div key={s.id} className="col-lg-6">
              {active === 'timeseries' && <TimeSeriesCard sample={s} />}
              {active === 'dissertations' && <DissertationCard sample={s} />}
              {active === 'assignments' && <AssignmentCard sample={s} />}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

const chipStyle = {
  background: 'rgba(255,255,255,0.2)',
  color: '#fff',
  borderRadius: 20,
  padding: '2px 9px',
  fontSize: '0.75rem',
};

function DissertationCard({ sample }) {
  return (
    <div className="card h-100" style={{ overflow: 'hidden' }}>
      <div
        style={{
          background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
          color: '#fff',
          padding: '1.4rem',
        }}
      >
        <div className="mb-2">
          {sample.research_area && (
            <span className="me-1" style={chipStyle}>
              {safeText(sample.research_area)}
            </span>
          )}
          <span className="me-1" style={chipStyle}>
            {safeText(sample.chapter_count)} chapters
          </span>
          {sample.created_at && (
            <span style={chipStyle}>{new Date(sample.created_at).toLocaleDateString()}</span>
          )}
        </div>
        <h5 style={{ fontWeight: 700, marginBottom: 0 }}>{safeText(sample.topic, '(untitled)')}</h5>
      </div>
      <div className="card-body d-flex flex-column">
        <p className="text-muted small flex-grow-1">
          A full dissertation with literature review, methodology, analysis, and references.
        </p>
        <div className="d-flex gap-2 flex-wrap">
          <Link to={`/dissertation/${sample.id}`} className="btn btn-primary btn-sm flex-grow-1">
            <i className="fas fa-eye me-1"></i>View
          </Link>
          <DownloadButton
            href={`/dissertation/${sample.id}/download/pdf`}
            filename={`Sample_${sample.id}.pdf`}
            icon="fa-file-pdf"
            label=""
            className="btn btn-outline-secondary btn-sm"
          />
          <DownloadButton
            href={`/dissertation/${sample.id}/download/docx`}
            filename={`Sample_${sample.id}.docx`}
            icon="fa-file-word"
            label=""
            className="btn btn-outline-secondary btn-sm"
          />
        </div>
      </div>
    </div>
  );
}

function AssignmentCard({ sample }) {
  return (
    <div className="card h-100" style={{ overflow: 'hidden' }}>
      <div
        style={{
          background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
          color: '#fff',
          padding: '1.4rem',
        }}
      >
        <div className="mb-2">
          <span className="me-1" style={chipStyle}>
            {safeText(sample.assignment_type, 'Essay')}
          </span>
          {sample.academic_level && (
            <span className="me-1" style={chipStyle}>{safeText(sample.academic_level)}</span>
          )}
          {sample.referencing_style && (
            <span className="me-1" style={chipStyle}>{safeText(sample.referencing_style).toUpperCase()}</span>
          )}
          {sample.word_count ? (
            <span style={chipStyle}>{Number(sample.word_count).toLocaleString()} words</span>
          ) : null}
        </div>
        <h5 style={{ fontWeight: 700, marginBottom: 0 }}>{safeText(sample.topic, '(untitled)')}</h5>
      </div>
      <div className="card-body d-flex flex-column">
        <p className="text-muted small flex-grow-1">
          An essay with an introduction, body, conclusion, and references.
        </p>
        <div className="d-flex gap-2 flex-wrap">
          <Link to={`/assignment/${sample.id}`} className="btn btn-primary btn-sm flex-grow-1">
            <i className="fas fa-eye me-1"></i>View
          </Link>
          <DownloadButton
            href={`/assignment/${sample.id}/download/pdf`}
            filename={`Sample_Assignment_${sample.id}.pdf`}
            icon="fa-file-pdf"
            label=""
            className="btn btn-outline-secondary btn-sm"
          />
          <DownloadButton
            href={`/assignment/${sample.id}/download/docx`}
            filename={`Sample_Assignment_${sample.id}.docx`}
            icon="fa-file-word"
            label=""
            className="btn btn-outline-secondary btn-sm"
          />
        </div>
      </div>
    </div>
  );
}

function TimeSeriesCard({ sample }) {
  return (
    <div className="card h-100" style={{ overflow: 'hidden' }}>
      <div
        style={{
          background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
          color: '#fff',
          padding: '1.4rem',
        }}
      >
        <div className="mb-2">
          <span className="me-1" style={chipStyle}>
            <i className="fas fa-globe-africa me-1"></i>
            {safeText(sample.country_name)}
          </span>
          {sample.start_year && sample.end_year && (
            <span className="me-1" style={chipStyle}>
              {safeText(sample.start_year)}–{safeText(sample.end_year)}
            </span>
          )}
          {sample.n_obs ? (
            <span style={chipStyle}>{safeText(sample.n_obs)} obs</span>
          ) : null}
        </div>
        <h5 style={{ fontWeight: 700, marginBottom: 0 }}>
          {safeText(sample.var1_name)} <span style={{ opacity: 0.75 }}>vs</span> {safeText(sample.var2_name)}
        </h5>
      </div>
      <div className="card-body d-flex flex-column">
        <p className="text-muted small flex-grow-1">
          Econometric analysis: stationarity tests, cointegration, Granger causality,
          VAR/VECM, impulse responses, forecast evaluation.
        </p>
        <div className="d-flex gap-2 flex-wrap">
          <Link to={`/worldbank/${sample.id}`} className="btn btn-primary btn-sm flex-grow-1">
            <i className="fas fa-eye me-1"></i>View
          </Link>
          <DownloadButton
            href={`/api/worldbank/analysis/${sample.id}/download/pdf`}
            filename={`Sample_TimeSeries_${sample.id}.pdf`}
            icon="fa-file-pdf"
            label=""
            className="btn btn-outline-secondary btn-sm"
          />
          <DownloadButton
            href={`/api/worldbank/analysis/${sample.id}/download/docx`}
            filename={`Sample_TimeSeries_${sample.id}.docx`}
            icon="fa-file-word"
            label=""
            className="btn btn-outline-secondary btn-sm"
          />
          <DownloadButton
            href={`/api/worldbank/analysis/${sample.id}/download/csv`}
            filename={`Sample_TimeSeries_${sample.id}.csv`}
            icon="fa-file-csv"
            label=""
            className="btn btn-outline-secondary btn-sm"
          />
        </div>
      </div>
    </div>
  );
}
